import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import { Bar, BarChart, XAxis, YAxis, ResponsiveContainer, ReferenceLine, Cell } from "recharts";
import { CycleTimeDistributionBucket } from "@/hooks/useCycleTimeAnalytics";
import { Skeleton } from "@/components/ui/skeleton";

interface CycleTimeDistributionChartProps {
  data: {
    buckets: CycleTimeDistributionBucket[];
    mean: number;
    median: number;
    percentUnder15Days: number;
  } | null;
  isLoading: boolean;
}

export function CycleTimeDistributionChart({ data, isLoading }: CycleTimeDistributionChartProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Cycle Time Distribution</CardTitle>
          <CardDescription>Distribution of total cycle times across completed jobs</CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[250px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.buckets.every(b => b.count === 0)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Cycle Time Distribution</CardTitle>
          <CardDescription>Distribution of total cycle times across completed jobs</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8">
            No data available for the selected period.
          </p>
        </CardContent>
      </Card>
    );
  }

  const chartData = data.buckets.map(bucket => ({
    ...bucket,
    fill: bucket.max <= 15 ? 'hsl(var(--primary))' : 'hsl(var(--primary) / 0.6)',
  }));

  const chartConfig = {
    count: {
      label: 'Jobs',
      color: 'hsl(var(--primary))',
    },
  };

  const maxCount = Math.max(...data.buckets.map(b => b.count));

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
          <div>
            <CardTitle>Cycle Time Distribution</CardTitle>
            <CardDescription>Distribution of total cycle times across completed jobs</CardDescription>
          </div>
          <div className="text-sm font-medium text-primary bg-primary/10 px-3 py-1.5 rounded-full">
            {data.percentUnder15Days.toFixed(0)}% under 15 days
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[250px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
            >
              <XAxis 
                dataKey="bucket" 
                axisLine={false}
                tickLine={false}
                fontSize={12}
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
              />
              <YAxis 
                axisLine={false}
                tickLine={false}
                fontSize={12}
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
                allowDecimals={false}
              />
              <ChartTooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const bucket = payload[0].payload as CycleTimeDistributionBucket;
                  return (
                    <div className="bg-popover border rounded-lg shadow-lg p-3 space-y-1">
                      <p className="font-medium">{bucket.bucket}</p>
                      <p className="text-sm">{bucket.count} jobs ({bucket.percentage.toFixed(0)}%)</p>
                    </div>
                  );
                }}
              />
              <Bar 
                dataKey="count" 
                radius={[4, 4, 0, 0]}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>

        {/* Stats */}
        <div className="flex justify-center gap-8 mt-4 pt-4 border-t">
          <div className="text-center">
            <p className="text-sm text-muted-foreground">Mean</p>
            <p className="text-lg font-semibold">{data.mean.toFixed(1)} days</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-muted-foreground">Median</p>
            <p className="text-lg font-semibold">{data.median.toFixed(1)} days</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
